import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { GALLERY_IMAGES } from '@/data/mockData';
import { GALLERY } from '@/constants/testIds';
import ParticlesBg from '@/components/common/ParticlesBg';

const FILTERS = [
  { key: 'all', label: 'All', tid: GALLERY.filterAll },
  { key: 'workshops', label: 'Workshops', tid: GALLERY.filterWorkshops },
  { key: 'events', label: 'Events', tid: GALLERY.filterEvents },
  { key: 'corporate', label: 'Corporate', tid: 'gallery-filter-corporate' },
  { key: 'awards', label: 'Awards', tid: GALLERY.filterAwards },
];

const Gallery = () => {
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const items = useMemo(() => {
    return filter === 'all' ? GALLERY_IMAGES : GALLERY_IMAGES.filter((i) => i.cat === filter);
  }, [filter]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  return (
    <>
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 radial-glow" />
        <ParticlesBg count={25} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/90 font-mono">Media Gallery</div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-4xl font-semibold">
            Moments that build the <span className="text-brand-cyan">BYMAT</span> story.
          </h1>
          <p className="mt-5 text-white/70 max-w-2xl">Workshops, events, awards, and corporate trainings — captured across the journey.</p>

          <div className="mt-8 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                data-testid={f.tid}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  filter === f.key
                    ? 'bg-brand-cyan text-black btn-glow'
                    : 'glass hover:bg-brand-cyan/10 hover:text-brand-cyan'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 [column-fill:_balance]">
            {items.map((img, i) => (
              <motion.button
                type="button"
                onClick={() => setLightbox(img)}
                key={img.url}
                data-testid={`${GALLERY.imageItem}-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.4 }}
                className="mb-4 block w-full break-inside-avoid rounded-2xl overflow-hidden glass hover:border-brand-cyan/40 transition group"
              >
                <div className="relative">
                  <img src={img.url} alt={img.caption} className="w-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
                  <div className="absolute bottom-3 left-3 right-3 text-sm font-medium opacity-0 group-hover:opacity-100 transition translate-y-2 group-hover:translate-y-0">
                    {img.caption}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button type="button" className="absolute top-6 right-6 h-10 w-10 rounded-lg glass flex items-center justify-center" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-5xl w-full">
            <img src={lightbox.url} alt={lightbox.caption} className="w-full max-h-[80vh] object-contain rounded-2xl" />
            <div className="mt-4 text-center text-white/80">{lightbox.caption}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default Gallery;
